import { biToast } from '@pulsepointinc/bi-components';
import { type Mutation, MutationCache, type Query, QueryCache, QueryClient } from '@tanstack/react-query';

export const DEFAULT_STALE_TIME = 60 * 1000; // 1 minute in ms
export const LONG_STALE_TIME = 60 * 60 * 1000; // 1 hour in ms
export const SHORT_STALE_TIME = 5 * 1000; // 5 seconds in ms

const DEFAULT_ERROR_TITLE = 'Server Error';
const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again later.';

function isServerError(status: number): boolean {
  return status >= 500;
}

function showDefaultErrorToast(error: Error) {
  if ('status' in error && isServerError(error.status as number)) {
    biToast.error(DEFAULT_ERROR_TITLE, DEFAULT_ERROR_MESSAGE);
  }
}

function handleQueryError(error: Error, query: Query<unknown, unknown, unknown, readonly unknown[]>) {
  const hasCustomHandler = query.options.meta?.onError;

  if (hasCustomHandler) {
    return;
  }

  if (query.state.data !== undefined) {
    showDefaultErrorToast(error);
  }
}

function handleMutationError(
  error: Error,
  _variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>,
) {
  const hasCustomHandler = mutation.options.onError;

  if (hasCustomHandler) {
    return;
  }

  showDefaultErrorToast(error);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: DEFAULT_STALE_TIME,
      retry: 1,
    },
  },
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleMutationError,
  }),
});
